import { flatten } from 'flat';

import {
    Locale,
    StringMap,
    ProgramTheme,
    DefaultMessages,
    Messages,
    ProgramThemeMessages,
    SegmentSpecificMessages,
    Segment,
    ViewModel,
    Country,
    StudentPersonalInfoViewModel,
    MessagesModule,
    RequestOrganizationMessages,
    RequestOrgMessagesModule,
    RequestOrganizationViewModel,
} from '../types/types';
import { assertValidLocale, isValidLocale } from '../types/assertions';
import { getOptions } from '../../options/options';
import { logger } from '../utils/logger/logger';
import { DEFAULT_LOCALE } from '../../constants';

import { messages as enUS } from './messages/en-US';
import { enUSRequestOrgMessages } from './messages/requestOrganization/en-US';

/**
 * @private
 * NOTE: webpack will not import chunks recursively, or with more than one variable in the path.
 * Here we're using a fully statically-defined import, for reliability.
 */
export const getMessagesModuleByLocale = async (locale: Locale): Promise<MessagesModule> => {
    let importPromise: Promise<any>;

    switch (locale) {
        case 'en-US':
            // Always have en-US available. Code-splitting is currently a problem for library consumers (HD-525)
            importPromise = Promise.resolve({ messages: enUS });
            break;
        // Example for how we want to do things once the library consumer code-split issue is resolved:
        // case 'fr':
            // TODO restore code-splitting once library users are able to serve it over the network from node_modules
            // importPromise = import(/* webpackChunkName: "messages_fr" */ './messages/fr');
            // break;
        default:
            logger.warn(`getMessagesModuleByLocale: Unable to load messages for ${locale}, falling back to en-US`);
            importPromise = Promise.resolve({ messages: enUS });
    }
    return importPromise.then(messagesModule => messagesModule.messages)
        .catch(() => logger.error(`Unable to load messages for ${locale}`));
};


/**
 * @private
 * NOTE: webpack will not import chunks recursively, or with more than one variable in the path.
 * Here we're using a fully statically-defined import, for reliability.
 */
export const getRequestOrgMessagesModuleByLocale = async (locale: Locale): Promise<any> => {
    switch (locale) {
        case 'ar':
            return import(/* webpackChunkName: "messages_ar" */ './messages/requestOrganization/ar').then(m => m.arRequestOrgMessages);
        case 'bg':
            return import(/* webpackChunkName: "messages_bg" */ './messages/requestOrganization/bg').then(m => m.bgRequestOrgMessages);
        case 'cz':
            return import(/* webpackChunkName: "messages_cz" */ './messages/requestOrganization/cz').then(m => m.czRequestOrgMessages);
        case 'da':
            return import(/* webpackChunkName: "messages_da" */ './messages/requestOrganization/da').then(m => m.daRequestOrgMessages);
        case 'de':
            return import(/* webpackChunkName: "messages_de" */ './messages/requestOrganization/de').then(m => m.deRequestOrgMessages);
        case 'el':
            return import(/* webpackChunkName: "messages_el" */ './messages/requestOrganization/el').then(m => m.elRequestOrgMessages);
        case 'en-GB':
            return import(/* webpackChunkName: "messages_en-GB" */ './messages/requestOrganization/en-GB').then(m => m.enGBRequestOrgMessages);
        case 'en-US':
            return enUSRequestOrgMessages;
        case 'es-ES':
            return import(/* webpackChunkName: "messages_es-ES" */ './messages/requestOrganization/es-ES').then(m => m.esESRequestOrgMessages);
        case 'es':
            return import(/* webpackChunkName: "messages_es" */ './messages/requestOrganization/es').then(m => m.esRequestOrgMessages);
        case 'fi':
            return import(/* webpackChunkName: "messages_fi" */ './messages/requestOrganization/fi').then(m => m.fiRequestOrgMessages);
        case 'fr-CA':
            return import(/* webpackChunkName: "messages_fr-CA" */ './messages/requestOrganization/fr-CA').then(m => m.frCARequestOrgMessages);
        case 'fr':
            return import(/* webpackChunkName: "messages_fr" */ './messages/requestOrganization/fr').then(m => m.frRequestOrgMessages);
        case 'hr':
            return import(/* webpackChunkName: "messages_hr" */ './messages/requestOrganization/hr').then(m => m.hrRequestOrgMessages);
        case 'hu':
            return import(/* webpackChunkName: "messages_hu" */ './messages/requestOrganization/hu').then(m => m.huRequestOrgMessages);
        case 'id':
            return import(/* webpackChunkName: "messages_id" */ './messages/requestOrganization/id').then(m => m.idRequestOrgMessages);
        case 'is':
            return import(/* webpackChunkName: "messages_is" */ './messages/requestOrganization/is').then(m => m.isRequestOrgMessages);
        case 'it':
            return import(/* webpackChunkName: "messages_it" */ './messages/requestOrganization/it').then(m => m.itRequestOrgMessages);
        case 'iw':
            return import(/* webpackChunkName: "messages_iw" */ './messages/requestOrganization/iw').then(m => m.iwRequestOrgMessages);
        case 'ja':
            return import(/* webpackChunkName: "messages_ja" */ './messages/requestOrganization/ja').then(m => m.jaRequestOrgMessages);
        case 'ko':
            return import(/* webpackChunkName: "messages_ko" */ './messages/requestOrganization/ko').then(m => m.koRequestOrgMessages);
        case 'lt':
            return import(/* webpackChunkName: "messages_lt" */ './messages/requestOrganization/lt').then(m => m.ltRequestOrgMessages);
        case 'ms':
            return import(/* webpackChunkName: "messages_ms" */ './messages/requestOrganization/ms').then(m => m.msRequestOrgMessages);
        case 'nl':
            return import(/* webpackChunkName: "messages_nl" */ './messages/requestOrganization/nl').then(m => m.nlRequestOrgMessages);
        case 'no':
            return import(/* webpackChunkName: "messages_no" */ './messages/requestOrganization/no').then(m => m.noRequestOrgMessages);
        case 'pl':
            return import(/* webpackChunkName: "messages_pl" */ './messages/requestOrganization/pl').then(m => m.plRequestOrgMessages);
        case 'pt-BR':
            return import(/* webpackChunkName: "messages_pt-BR" */ './messages/requestOrganization/pt-BR').then(m => m.ptBRRequestOrgMessages);
        case 'pt':
            return import(/* webpackChunkName: "messages_pt" */ './messages/requestOrganization/pt').then(m => m.ptRequestOrgMessages);
        case 'ro':
            return import(/* webpackChunkName: "messages_ro" */ './messages/requestOrganization/ro').then(m => m.roRequestOrgMessages);
        case 'ru':
            return import(/* webpackChunkName: "messages_ru" */ './messages/requestOrganization/ru').then(m => m.ruRequestOrgMessages);
        case 'sk':
            return import(/* webpackChunkName: "messages_sk" */ './messages/requestOrganization/sk').then(m => m.skRequestOrgMessages);
        case 'sl':
            return import(/* webpackChunkName: "messages_sl" */ './messages/requestOrganization/sl').then(m => m.slRequestOrgMessages);
        case 'sr':
            return import(/* webpackChunkName: "messages_sr" */ './messages/requestOrganization/sr').then(m => m.srRequestOrgMessages);
        case 'sv':
            return import(/* webpackChunkName: "messages_sv" */ './messages/requestOrganization/sv').then(m => m.svRequestOrgMessages);
        case 'tr':
            return import(/* webpackChunkName: "messages_tr" */ './messages/requestOrganization/tr').then(m => m.trRequestOrgMessages);
        case 'zh-HK':
            return import(/* webpackChunkName: "messages_zh-HK" */ './messages/requestOrganization/zh-HK').then(m => m.zhHKRequestOrgMessages);
        case 'zh':
            return import(/* webpackChunkName: "messages_zh" */ './messages/requestOrganization/zh').then(m => m.zhRequestOrgMessages);
        default:
            logger.warn(`getRequestOrgMessagesModuleByLocale: Unable to load messages for ${locale}, falling back to en-US`);
            return enUSRequestOrgMessages;
    }
};

/**
 * @description Pluck only the messages we want to override defaults with.
 *      Older versions of the theme response included all messages, but we don't want to overwrite all defaults.
 * @private
 */
export const getThemeMessages = (msgs: ProgramThemeMessages): ProgramThemeMessages => {
    return {
        companyName: msgs.companyName,
        step: {
            personalInfo: {
                title: msgs.step.personalInfo.title,
            },
            success: {
                redirectUrl: msgs.step.success.redirectUrl,
                redirectButtonText: msgs.step.success.redirectButtonText,
                subtitle: msgs.step.success.subtitle,
                title: msgs.step.success.title,
            },
            docUpload: {
                title: msgs.step.docUpload.title,
                subtitle: msgs.step.docUpload.subtitle,
            },
        },
    };
};

export const getOptionsMessages = (locale: Locale): Object => {
    let optionalMessages: Object = {};
    if (getOptions().messagesWithLocale[locale]) {
        optionalMessages = getOptions().messagesWithLocale[locale];
    }
    return optionalMessages;
};

/**
 * @todo tests
 * @private
 */
export const countryToLocale = (country: Country): Locale => {
    let locale: Locale;
    switch (country) {
        case 'US':
            locale = 'en-US';
        default:
            locale = 'en-US';
    }
    return locale;
};


/**
 * @todo tests
 * @description Get all messages for a given locale.
 *      Return a flattened messages object, where properties are dot-string style.
 * @private
 */
export const getMessages = async (locale: Locale, programThemeMessages: ProgramThemeMessages, segment: Segment): Promise<Messages> => {
    let usedLocale = locale;

    if (!(isValidLocale(usedLocale))) {
        logger.error(`Invalid locale ${usedLocale}, falling back to en-US`);
        usedLocale = 'en-US';
    }

    let defaultMessages: DefaultMessages | object = {};
    let segmentSpecificMessages: SegmentSpecificMessages | object = {};
    try {
        const messagesModule: MessagesModule = await getMessagesModuleByLocale(usedLocale);
        defaultMessages = messagesModule.defaultMessages;
        segmentSpecificMessages = messagesModule.segmentMessages[segment];
    } catch (e) {
        logger.error(e);
    }

    const cleanThemeMessages = getThemeMessages(programThemeMessages);

    // Flatten these before object.assign so they are dot-props, so nested objects aren't overwritten, deleting properties
    const messages: Messages = Object.assign(
        {} as Messages,
        defaultMessages ? flatten(defaultMessages) : {},
        cleanThemeMessages ? flatten(cleanThemeMessages) : {},
        segmentSpecificMessages ? flatten(segmentSpecificMessages) : {},
    );
    return messages;
};

/**
 * @todo tests
 * @description Get all messages for a given locale.
 *      Return a flattened messages object, where properties are dot-string style.
 * @private
 */
export const getRequestOrgMessages = async (locale: Locale): Promise<RequestOrganizationMessages> => {
    let requestOrganizationMessages: RequestOrganizationMessages | object = {};

    try {
        const requestOrganizationMessagesModule: RequestOrgMessagesModule = await getRequestOrgMessagesModuleByLocale(locale);

        requestOrganizationMessages = requestOrganizationMessagesModule;
    } catch (e) {
        logger.error(e);
    }

    const optionsMessages = getOptionsMessages(locale);

    // Flatten these before object.assign so they are dot-props, so nested objects aren't overwritten, deleting properties
    const messages: RequestOrganizationMessages = Object.assign(
        {} as RequestOrganizationMessages,
        requestOrganizationMessages ? flatten(requestOrganizationMessages) : {},
        optionsMessages ? flatten(optionsMessages) : {},
    );
    return messages;
};

/**
 * @description
 */
export const getLocaleSafely = (viewModel: ViewModel | RequestOrganizationViewModel): Locale => {
    let returnedLocale: Locale = 'en-US'; // default
    let newLocale: Locale;

    let chosenLocale: Locale;
    let country: Country;

    try {
        // Only in specific circumstances will we receive a locale choice
        chosenLocale = (viewModel as StudentPersonalInfoViewModel).localeChoice.value;
    } catch (e) {
        // Don't throw for property access errors here... they may not have existed (by design)
    }

    try {
        // Only in specific circumstances will we receive a country choice
        country = (viewModel as StudentPersonalInfoViewModel).countryChoice.value;
    } catch (e) {
        // Don't throw for property access errors here... they may not have existed (by design)
    }

    if (chosenLocale) {
        newLocale = chosenLocale;
    } else if (country) {
        newLocale = countryToLocale(country);
    }

    if (newLocale) {
        // If we _did_ get it, make sure it's valid
        returnedLocale = newLocale;
    }

    return returnedLocale;
};

/**
 * @deprecated Use `getMessages()` instead
 */
export const getIntlMessages = (locale: Locale, programTheme: ProgramTheme = null): StringMap => {
    assertValidLocale(locale);

    let optionalMessages: Object = {};
    if (getOptions().messagesWithLocale[locale]) {
        optionalMessages = getOptions().messagesWithLocale[locale];
    } else if (getOptions().messagesWithLocale[DEFAULT_LOCALE]) {
        optionalMessages = getOptions().messagesWithLocale[DEFAULT_LOCALE];
    }

    let sheerIdMessages: Object = {};
    if (programTheme) {
        try {
            sheerIdMessages = programTheme.intl.messages;
        } catch (e) {
            logger.error('Error accessing programTheme.intl.messages');
        }
    }
    // react-intl doesn't allow the messages object to contain nested objects.
    // Use flatten() so we can access nested objects with dot-strings like 'foo.bar'
    return flatten(Object.assign({}, sheerIdMessages, optionalMessages)) as StringMap;
};

export const getLocale = (programTheme: ProgramTheme = null): Locale => {
    let locale: Locale = getOptions().locale;

    if (programTheme) {
        try {
            locale = programTheme.intl.locale as Locale;
        } catch (e) {
            logger.error('Error accessing programTheme.intl.locale. Did the shape of an object/type change?');
        }
        if (!isValidLocale(locale)) {
            logger.warn(`Invalid locale "${locale}" received from server. Using fallback "${getOptions().locale}" instead.`);
            locale = getOptions().locale;
        }
    }
    return locale;
};

export const getRequestOrganizationLocale = (programTheme: ProgramTheme = null): Locale => {
    const urlParams = new URLSearchParams(window.location.search);
    let locale: Locale;

    if (urlParams.get('locale')) {
        locale = urlParams.get('locale') as Locale;
    } else {
        locale = getOptions().locale;
    }

    if (programTheme) {
        try {
            locale = programTheme.intl.locale as Locale;
        } catch (e) {
            logger.error('Error accessing programTheme.intl.locale. Did the shape of an object/type change?');
        }
        if (!isValidLocale(locale)) {
            logger.warn(`Invalid locale "${locale}" received from server. Using fallback "${getOptions().locale}" instead.`);
            locale = getOptions().locale;
        }
    }

    if (!(isValidLocale(locale))) {
        logger.error(`Invalid locale ${locale}, falling back to en-US`);
        locale = 'en-US';
    }

    return locale;
};

/**
 * @deprecated
 */
export const setLocale = () => {
    logger.error('setLocale() is deprecated, use verificationService.updateLocale() instead.');
};
